import { type FeatureCategory, type PromoFeature } from '@luxuryescapes/contract-svc-promo';

export type PromoFeatureIndicator = PromoFeature & {
  category: FeatureCategory;
  depth: number;
  parentKeys: ReadonlyArray<string>;
};

function getParentFeatureKey(feature: PromoFeature): string | undefined {
  return feature.tags?.find((tag) => tag.type === 'parent-feature')?.key;
}

/**
 * The service catalogue is flat: child features point at their parent via a
 * `parent-feature` tag and inherit the category of their root. This rebuilds
 * the display hierarchy, listing each feature directly after its parent.
 */
export function getPromoFeatureIndicators(features: ReadonlyArray<PromoFeature>): ReadonlyArray<PromoFeatureIndicator> {
  const featureKeys = new Set(features.map((feature) => feature.key));
  const childrenByParentKey = new Map<string, Array<PromoFeature>>();
  const roots: Array<PromoFeature> = [];

  features.forEach((feature) => {
    const parentKey = getParentFeatureKey(feature);

    // Features whose parent is missing from the catalogue are shown as roots rather than dropped.
    if (parentKey == null || parentKey === feature.key || !featureKeys.has(parentKey)) {
      roots.push(feature);
      return;
    }

    childrenByParentKey.set(parentKey, [...(childrenByParentKey.get(parentKey) ?? []), feature]);
  });

  const indicators: Array<PromoFeatureIndicator> = [];
  const visitedKeys = new Set<string>();

  function visit(feature: PromoFeature, category: FeatureCategory, depth: number, parentKeys: ReadonlyArray<string>) {
    if (visitedKeys.has(feature.key)) {
      return;
    }

    visitedKeys.add(feature.key);
    indicators.push({ ...feature, category, depth, parentKeys });

    (childrenByParentKey.get(feature.key) ?? []).forEach((child) => {
      visit(child, category, depth + 1, [...parentKeys, feature.key]);
    });
  }

  roots.forEach((feature) => visit(feature, feature.category ?? 'cross-cutting', 0, []));
  // Features caught in a parent cycle are never reached from a root; list them as roots too.
  features.forEach((feature) => visit(feature, feature.category ?? 'cross-cutting', 0, []));
  return indicators;
}

export function findPromoFeatureIndicator(
  features: ReadonlyArray<PromoFeature>,
  featureKey: string,
): PromoFeatureIndicator | undefined {
  return getPromoFeatureIndicators(features).find((feature) => feature.key === featureKey);
}

export function getOptInFeatureKeys(features: ReadonlyArray<PromoFeature>): ReadonlySet<string> {
  return new Set(getPromoFeatureIndicators(features).flatMap((feature) => (feature.canOptIn ? [feature.key] : [])));
}
